import React, { useState } from 'react';
import Button from '../../../../components/form-control/Button';
import SelectDropdown from '../../../../components/form-control/SelectDropdown';
import TextArea from '../../../../components/form-control/TextArea';
import usePolicy from '../../../../hooks/policyHook';
import { Policy } from '../../../../models/policy/Policy';
import { POLICY_REVIEW_STATES } from '../../../../resources/enums';
import { IRequestResponse, IReviewPolicyDTO } from '../../../../resources/interfaces';
import { getBooleanSelectDropdownList, isFleetPolicy, showToast } from '../../../../utils/miscFunctions';
import { FleetPolicy } from '../../../../models/policy/FleetPolicy';
import { changeFleetPolicyReviewState } from '../../../../api/requests';

interface IReviewPolicy {
  policy: Policy | FleetPolicy | undefined;
  onProceed: () => void;
  onCancel: () => void;
  type: POLICY_REVIEW_STATES;
  title: string;
  titleIcon: React.ReactNode;
  request?: boolean;
}

const ReviewPolicy = ({ policy, onProceed, onCancel, type, titleIcon, title, request }: IReviewPolicy) => {
  const { reviewPolicy, cancelPolicy } = usePolicy();
  const [submitting, setSubmitting] = useState<boolean>(false);
  // Per the Broker–Insurer Policy Status Flow spec, a broker-initiated
  // Cancel must be permanent — payment has already been received, so the
  // policy can't fall back to Draft. The "Terminate" checkbox that used
  // to expose the soft-cancel (IN_REVIEW) path is no longer rendered for
  // brokers, but we default the flag to true so that even if a stale
  // copy of the modal renders it, the request still goes terminal.
  const [terminatePolicy, setTerminatePolicy] = useState<boolean>(true);
  const [reviewReason, setReviewReason] = useState<string>('');
  const [allInfoAccurate, setAllInfoAccurate] = useState<string | undefined>(undefined);

  const getReviewEndpoint = (type: POLICY_REVIEW_STATES) => {
    
    const payload: IReviewPolicyDTO = {
      reason: reviewReason,
      reviewStage: type,
    };

    setSubmitting(true);

    let promise;

    if (isFleetPolicy(policy)) {
      switch (type) {
        case POLICY_REVIEW_STATES.CANCELLED:
          promise = request ? changeFleetPolicyReviewState(policy?.fleetNumber ?? '', payload) : cancelPolicy(policy?.fleetNumber ?? '', reviewReason, terminatePolicy);
          break;
        case POLICY_REVIEW_STATES.DUE_LATER:
        case POLICY_REVIEW_STATES.ON_HOLD:
        case POLICY_REVIEW_STATES.IN_REVIEW:
        default:
          promise = changeFleetPolicyReviewState(policy?.fleetNumber ?? '', payload);
          break;
      }
    } else {
      switch (type) {
        case POLICY_REVIEW_STATES.CANCELLED:
          promise = request ? reviewPolicy(policy?.ref ?? '', payload) : cancelPolicy(policy?.ref ?? '', reviewReason, terminatePolicy);
          break;
        case POLICY_REVIEW_STATES.DUE_LATER:
        case POLICY_REVIEW_STATES.ON_HOLD:
        case POLICY_REVIEW_STATES.IN_REVIEW:
        default:
          promise = reviewPolicy(policy?.ref ?? '', payload);
          break;
      }
    }



    promise
      .then((response: any) => {
        showToast.success(response.message);
        onProceed();
      })
      .catch((error: IRequestResponse) => {
        showToast.error(error.detail || error?.description);
      })
      .finally(() => setSubmitting(false));
  };

  const handleSubmit = () => {
    if (type === POLICY_REVIEW_STATES.IN_REVIEW && allInfoAccurate !== 'yes') {
      showToast.error('You need to confirm all information provided is accurate to proceed.');
      return;
    }

    if (type !== POLICY_REVIEW_STATES.IN_REVIEW && !reviewReason) {
      showToast.error('You need to provide a reason to proceed.');
      return;
    }

    getReviewEndpoint(type);
  };

  const getDescription = (): string => {
    if (request) {
      return 'You are submitting this policy for cancellation, provide reasons or concerns.';
    }

    switch (type) {
      case POLICY_REVIEW_STATES.DUE_LATER:
        return 'If you want to set this policy to due later, please provide reasons or concerns about the policy.';
      case POLICY_REVIEW_STATES.CANCELLED:
        return 'By cancelling this policy, you agree to have this policy cancelled by the National Commission/Motor Insurance Department. Provide reasons or concerns for the cancellation of this policy.';
      case POLICY_REVIEW_STATES.ON_HOLD:
        return 'If you want to put this policy on hold, please provide reasons or concerns about the policy.';
      case POLICY_REVIEW_STATES.IN_REVIEW:
        return `By submitting this policy for review, you agree that all of the details provided by the policy's owner are accurate as of this day. Do you agree to submit this?`;
      default:
        return '';
    }
  };

  return (
    <div className="bg-white rounded-xl dark:bg-darkBg">

      <div className="p-4">
        <div className="py-2 space-y-4">
          <p className='dark:text-darkBg-mainText'>{getDescription()}</p>

          {type === POLICY_REVIEW_STATES.IN_REVIEW && (
            <SelectDropdown
              searchPlaceHolder="Click to select"
              name="goodRepairState"
              listSelectedValue={allInfoAccurate}
              onChange={(val: string | undefined) => setAllInfoAccurate(val)}
              list={getBooleanSelectDropdownList()}
              placeholder=""
            />
          )}

          {type === POLICY_REVIEW_STATES.CANCELLED && (
            <div className="space-y-5">
              <TextArea
                name={'reason'}
                value={reviewReason}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setReviewReason(event.target.value)}
                placeholder="Write your reasons here."
                rows={5}
              />
              {/* The "Terminate" checkbox used to let a broker accidentally
                  toggle this from a permanent cancel to a soft IN_REVIEW.
                  The spec says cancels at Pending Approval are always
                  permanent, so the toggle is removed and terminatePolicy
                  is hard-pinned to true via its initial state above. */}
            </div>
          )}

          {!(type === POLICY_REVIEW_STATES.CANCELLED || type === POLICY_REVIEW_STATES.IN_REVIEW) && (
            <TextArea
              name={'reason'}
              value={reviewReason}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setReviewReason(event.target.value)}
              placeholder="Write your reasons here."
              rows={5}
            />
          )}
        </div>

        <div className="w-full flex gap-4 mt-6 !mb-4">
          <Button
            label="Cancel"
            onClick={onCancel}
            bgColor='secondary'
            fullWidth
          />
          <Button
            label="Confirm"
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting}
            fullWidth
            bgColor='primary'
          />
        </div>
      </div>
    </div>
  );
};

export default ReviewPolicy;
